import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useFavourite } from '@app/hooks';
import {
  FavouritesWrapper,
  FavouritesScrollContainer,
  ScrollButton,
  EmptyMessage,
} from './styles';
import FavListItem from '@/app/components/Trade/FavListitem/index';
import { useTranslation } from 'react-i18next';

const TradeFavourites = () => {
  const { data: favourites, updateFavouriteOrder } = useFavourite();
  const { t } = useTranslation();

  // 드래그 앤 드롭 상태
  const [activeId, setActiveId] = useState<string | number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // 스크롤 관련 상태
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // 북마크 아이템에 안전한 드래그 ID 생성
  const favouritesWithDragId = useMemo(() => {
    return (
      favourites?.map((item, index) => {
        const dragId = item.id || item.ep_id || `bookmark-${index}`;
        return {
          ...item,
          dragId: String(dragId),
        };
      }) || []
    );
  }, [favourites]);

  // 드래그 앤 드롭 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 드래그 시작
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  }, []);

  // 드래그 종료
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      setActiveId(null);
      setIsDragging(false);

      if (active.id !== over?.id) {
        const oldIndex = favouritesWithDragId.findIndex(
          (item) => item.dragId === active.id
        );
        const newIndex = favouritesWithDragId.findIndex(
          (item) => item.dragId === over?.id
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newFavourites = arrayMove(
            favouritesWithDragId,
            oldIndex,
            newIndex
          );
          // 원본 데이터로 변환 (dragId 제거)
          const originalFormat = newFavourites.map((item: any) => {
            const { dragId, ...originalItem } = item;
            return originalItem;
          });
          updateFavouriteOrder(originalFormat);
        }
      }
    },
    [favouritesWithDragId, updateFavouriteOrder]
  );

  // 현재 드래그 중인 아이템
  const activeItem = useMemo(() => {
    return favouritesWithDragId?.find((item) => item.dragId === activeId);
  }, [activeId, favouritesWithDragId]);

  // 스크롤 상태 확인
  const checkScrollButtons = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // 좌측 스크롤
  const scrollLeft = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollAmount = 200;

    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  // 우측 스크롤
  const scrollRight = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollAmount = 200;

    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  // 북마크 데이터나 컨테이너 크기 변경 시 스크롤 버튼 상태 확인
  useEffect(() => {
    checkScrollButtons();

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);

      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [favouritesWithDragId, checkScrollButtons]);

  // 즐겨찾기가 없어도 영역은 표시
  const isEmpty = !favourites || favourites.length === 0;

  return (
    <FavouritesWrapper>
      {isEmpty ? (
        <EmptyMessage>{t('favouriteCoinNotAddYet')}</EmptyMessage>
      ) : (
        <>
          {/* 좌측 스크롤 버튼 */}
          {canScrollLeft && (
            <ScrollButton
              direction="left"
              onClick={scrollLeft}
              disabled={!canScrollLeft}
            >
              <IconChevronLeft size={18} />
            </ScrollButton>
          )}

          {/* 우측 스크롤 버튼 */}
          {canScrollRight && (
            <ScrollButton
              direction="right"
              onClick={scrollRight}
              disabled={!canScrollRight}
            >
              <IconChevronRight size={18} />
            </ScrollButton>
          )}

          <FavouritesScrollContainer ref={scrollContainerRef}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={favouritesWithDragId.map((item) => item.dragId)}
                strategy={horizontalListSortingStrategy}
              >
                {favouritesWithDragId.map((item: any) => (
                  <FavListItem
                    key={item.dragId}
                    item={item}
                    isDragging={isDragging}
                  />
                ))}
              </SortableContext>
              {/* <DragOverlay>
                {activeItem ? (
                  <FavListItem item={activeItem} isDragging={true} />
                ) : null}
              </DragOverlay> */}
            </DndContext>
          </FavouritesScrollContainer>
        </>
      )}
    </FavouritesWrapper>
  );
};

export default React.memo(TradeFavourites);
